import { createRemoteJWKSet, jwtVerify, type JWTPayload } from "jose";

interface DiscoveryDocument {
  issuer: string;
  jwks_uri: string;
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint?: string;
  code_challenge_methods_supported?: string[];
  authorization_response_iss_parameter_supported?: boolean;
}

export interface WebSession {
  accessToken: string;
  identityClaims: Record<string, unknown> | null;
}

const OIDC_ISSUER = import.meta.env.VITE_OIDC_ISSUER ?? "https://oidc.sessapps.com";
const OIDC_CLIENT_ID = import.meta.env.VITE_OIDC_CLIENT_ID ?? "";
const REDIRECT_URI = import.meta.env.VITE_OIDC_REDIRECT_URI ?? `${window.location.origin}${window.location.pathname}`;
const SESSION_KEY = "kaikei.oidc.pending";
const PENDING_MAX_AGE_MS = 10 * 60 * 1000;

let session: WebSession | null = null;
let discovery: DiscoveryDocument | null = null;
let remoteKeys: ReturnType<typeof createRemoteJWKSet> | null = null;

interface PendingLogin {
  state: string;
  nonce: string;
  verifier: string;
  issuer: string;
  clientId: string;
  redirectUri: string;
  createdAt: number;
}

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function randomToken(byteLength = 32): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(byteLength)));
}

async function codeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

function httpsUrl(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password && !url.search && !url.hash;
  } catch {
    return false;
  }
}

function redirectUri(): string {
  let parsed: URL;
  try {
    parsed = new URL(REDIRECT_URI, window.location.origin);
  } catch {
    throw new Error("OIDC redirect URI の設定が不正です。");
  }
  if (parsed.origin !== window.location.origin || parsed.hash) {
    throw new Error("OIDC redirect URI は現在のWeb Originに完全一致させてください。");
  }
  if (window.location.protocol === "https:" && parsed.protocol !== "https:") {
    throw new Error("HTTPSのWebページではHTTPS redirect URIが必要です。");
  }
  return parsed.toString();
}

async function loadDiscovery(): Promise<DiscoveryDocument> {
  if (discovery) return discovery;
  if (!httpsUrl(OIDC_ISSUER)) throw new Error("OIDC issuer はHTTPS URLである必要があります。");
  const issuerBase = OIDC_ISSUER.endsWith("/") ? OIDC_ISSUER : `${OIDC_ISSUER}/`;
  const discoveryUrl = new URL(".well-known/openid-configuration", issuerBase).toString();
  const response = await fetch(discoveryUrl, {
    headers: { accept: "application/json" },
    credentials: "omit",
  });
  if (!response.ok) throw new Error("OIDC の設定を取得できませんでした。");
  const value = (await response.json()) as Partial<DiscoveryDocument>;
  if (
    value.issuer !== OIDC_ISSUER ||
    !httpsUrl(value.issuer) ||
    !httpsUrl(value.jwks_uri) ||
    !httpsUrl(value.authorization_endpoint) ||
    !httpsUrl(value.token_endpoint) ||
    !value.code_challenge_methods_supported?.includes("S256")
  ) {
    throw new Error("OIDC issuer または endpoint が一致しません。");
  }
  discovery = value as DiscoveryDocument;
  return discovery;
}

export function currentSession(): WebSession | null {
  return session;
}

export function configuredClient(): boolean {
  return OIDC_CLIENT_ID.length > 0;
}

export async function beginLogin(): Promise<void> {
  if (!OIDC_CLIENT_ID) throw new Error("VITE_OIDC_CLIENT_ID が設定されていません。");
  const metadata = await loadDiscovery();
  const configuredRedirectUri = redirectUri();
  const state = randomToken(24);
  const nonce = randomToken(24);
  const verifier = randomToken(48);
  const pending: PendingLogin = {
    state,
    nonce,
    verifier,
    issuer: metadata.issuer,
    clientId: OIDC_CLIENT_ID,
    redirectUri: configuredRedirectUri,
    createdAt: Date.now(),
  };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(pending));
  const query = new URLSearchParams({
    response_type: "code",
    client_id: OIDC_CLIENT_ID,
    redirect_uri: configuredRedirectUri,
    scope: "openid profile email",
    state,
    nonce,
    code_challenge: await codeChallenge(verifier),
    code_challenge_method: "S256",
  });
  window.location.assign(`${metadata.authorization_endpoint}?${query.toString()}`);
}

export async function completeLogin(): Promise<boolean> {
  const params = new URLSearchParams(window.location.search);
  const callbackError = params.get("error");
  if (callbackError) {
    sessionStorage.removeItem(SESSION_KEY);
    throw new Error("OIDC認証を完了できませんでした。");
  }
  const code = params.get("code");
  if (!code) return false;
  const expectedState = params.get("state");
  const pendingRaw = sessionStorage.getItem(SESSION_KEY);
  if (!pendingRaw) throw new Error("OIDC state が見つかりません。もう一度ログインしてください。");
  let pending: PendingLogin;
  try {
    const parsed: unknown = JSON.parse(pendingRaw);
    if (!parsed || typeof parsed !== "object") throw new Error("invalid pending login");
    const value = parsed as Record<string, unknown>;
    if (
      typeof value.state !== "string" ||
      typeof value.nonce !== "string" ||
      typeof value.verifier !== "string" ||
      typeof value.issuer !== "string" ||
      typeof value.clientId !== "string" ||
      typeof value.redirectUri !== "string" ||
      typeof value.createdAt !== "number"
    ) throw new Error("invalid pending login");
    pending = value as unknown as PendingLogin;
  } catch {
    throw new Error("OIDC state が壊れています。もう一度ログインしてください。");
  }
  if (!expectedState || expectedState !== pending.state) throw new Error("OIDC state の検証に失敗しました。");
  const metadata = await loadDiscovery();
  const configuredRedirectUri = redirectUri();
  if (
    pending.issuer !== metadata.issuer ||
    pending.clientId !== OIDC_CLIENT_ID ||
    pending.redirectUri !== configuredRedirectUri ||
    Date.now() - pending.createdAt > PENDING_MAX_AGE_MS
  ) {
    sessionStorage.removeItem(SESSION_KEY);
    throw new Error("OIDCログインの有効期限が切れています。もう一度お試しください。");
  }
  const callbackIssuer = params.get("iss");
  if ((metadata.authorization_response_iss_parameter_supported && callbackIssuer !== metadata.issuer) || (callbackIssuer && callbackIssuer !== metadata.issuer)) {
    sessionStorage.removeItem(SESSION_KEY);
    throw new Error("OIDC issuer の検証に失敗しました。");
  }
  const tokenResponse = await fetch(metadata.token_endpoint, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded", accept: "application/json" },
    credentials: "omit",
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: OIDC_CLIENT_ID,
      code,
      redirect_uri: configuredRedirectUri,
      code_verifier: pending.verifier,
    }),
  });
  if (!tokenResponse.ok) throw new Error("OIDC トークン交換に失敗しました。");
  const token = (await tokenResponse.json()) as { access_token?: unknown; id_token?: unknown };
  if (typeof token.access_token !== "string" || token.access_token.length === 0) throw new Error("OIDC access token がありません。");
  let identityClaims: Record<string, unknown> | null = null;
  if (typeof token.id_token === "string") {
    if (!remoteKeys) remoteKeys = createRemoteJWKSet(new URL(metadata.jwks_uri));
    let verified: { payload: JWTPayload };
    try {
      verified = await jwtVerify(token.id_token, remoteKeys, {
        issuer: metadata.issuer,
        audience: OIDC_CLIENT_ID,
        algorithms: ["RS256", "PS256", "ES256", "EdDSA"],
        requiredClaims: ["exp", "iat", "sub", "nonce"],
      });
    } catch {
      sessionStorage.removeItem(SESSION_KEY);
      throw new Error("OIDC ID token の検証に失敗しました。");
    }
    if (verified.payload.nonce !== pending.nonce) {
      sessionStorage.removeItem(SESSION_KEY);
      throw new Error("OIDC nonce の検証に失敗しました。");
    }
    identityClaims = Object.fromEntries(Object.entries(verified.payload));
  }
  session = { accessToken: token.access_token, identityClaims };
  sessionStorage.removeItem(SESSION_KEY);
  const cleanURL = new URL(configuredRedirectUri);
  window.history.replaceState({}, document.title, `${cleanURL.pathname}${cleanURL.search}`);
  return true;
}

export async function logout(): Promise<void> {
  session = null;
  const metadata = await loadDiscovery();
  if (metadata.end_session_endpoint) {
    const query = new URLSearchParams({ post_logout_redirect_uri: redirectUri() });
    const logoutURL = new URL(metadata.end_session_endpoint);
    logoutURL.search = query.toString();
    window.location.assign(logoutURL.toString());
  }
}
